import React, { useState, useEffect, useCallback } from 'react';
import { APP_CONFIG } from 'Config';
import { debounce, delay } from 'lodash';
import { usePage } from 'context/PageContext';
import './styles.scss';

const Facebook = '/static/images/ShareIcons/Facebook.svg';
const Twitter = '/static/images/ShareIcons/Twitter.svg';
const Pinterest = '/static/images/ShareIcons/Pinterest.svg';
const LinkedIn = '/static/images/ShareIcons/LinkedIn.svg';
const Email = '/static/images/ShareIcons/Email.svg';

interface ShareProps {
    page: string | number;
    text?: string;
    extendedDebounce?: boolean;
    component?: string;
}

const Share: React.FC<ShareProps> = ({ 
    page, 
    text = '', 
    extendedDebounce = false, 
    component = '',
}) => {
    const { pageData } = usePage();
    const [open, setOpen] = useState(false);
    const [renderIcons, setRenderIcons] = useState(false);
    const [shareText, setShareText] = useState(text);
    const [sharePage, setSharePage] = useState<string>();

    useEffect(() => {
        let url = `${APP_CONFIG.APP_URL.replace(/\/$/g, '')}/`;
        url = url.replace(/\//g, '%2F');

        switch (component) {
            case 'facility-homepage':
                url += 'facilities%2Fhomepage%2F';
                break;
            case 'auction-details':
                url += 'auctions%2Fdetail%2F';
                break;
            default:
                url += '';
        }

        setSharePage(`${url}${page}`);
        setShareText(text);

        if (open) {
            setRenderIcons(true);
        }
    }, [page, text, component, open]);

    const closeShareAfterDelay = useCallback(() => {
        const delayTime = extendedDebounce ? 19000 : 5000;
        const delayedClose = debounce(() => {
            setOpen(false);
            delay(() => setRenderIcons(false), 66.67);
        }, delayTime);

        delayedClose();

        return () => {
            delayedClose.cancel();
        };
    }, [extendedDebounce]);

    const openShare = () => {
        if (!open) {
            setRenderIcons(true);
            setOpen(true);
            closeShareAfterDelay();
        } else {
            setOpen(false);
            delay(() => setRenderIcons(false), 66.67);
        }
    };

    const openShareWindow = (href: string) => {
        window.open(href, 'newwindow', `width=575,height=480,top=${(window.innerHeight - 575) / 2},left=${(window.innerWidth - 480) / 2},toolbar=no,location=no,directories=no,status=no,menubar=no`);
        return false;
    };

    const renderIcon = (iconImage: string, iconAlt: string, className: string, url: string, isEmail = false) => {
        return (
            <a href={isEmail ? url : undefined} target={!isEmail ? '_blank' : undefined} rel={!isEmail ? 'noopener noreferrer' : undefined} title={`Share on ${iconAlt}`} className={`icon ${className}`} onClick={!isEmail ? () => openShareWindow(url) : undefined}>
                <img src={iconImage} alt={iconAlt} />
            </a>
        );
    };

    const renderShareBox = () => {
        if (!renderIcons) return null;

        const {
            image: image_url = `${APP_CONFIG.APP_URL}/static/images/logo_white_bg_social_media.jpg`,
            description = '',
        } = pageData.og || {};

        if (!open) return null;

        return (
            <div className={'share-popup-box d-flex justify-content-between align-items-center'}>
                <div className="share-label-container text-center">
                    <div className="share-label">
                        Share this
                        <br />
                        page on:
                    </div>
                </div>
                <div className="d-flex">
                    {renderIcon(Facebook, 'Facebook', 'facebook', `https://www.facebook.com/sharer/sharer.php?u=${sharePage}&quote=`)}
                    {renderIcon(Twitter, 'Twitter', 'twitter', `https://twitter.com/intent/tweet?source=${sharePage}&text=${sharePage}%0D%0A${shareText}`)}
                    {renderIcon(Pinterest, 'Pinterest', 'pinterest', `http://pinterest.com/pin/create/button/?media=${image_url}&url=${sharePage}&description=${description}`)}
                    {renderIcon(LinkedIn, 'LinkedIn', 'linked-in', `http://www.linkedin.com/shareArticle?mini=true&url=${sharePage}&title=${shareText}&summary=&source=${sharePage}`)}
                    {renderIcon(Email, 'Email', 'email', `mailto:?body=${sharePage}&subject=${shareText.replace(/\+/g, ' ')}`, true)}
                </div>
            </div>
        );
    };

    return (
        <div id="share-component" className="share-button">
            <i onClick={openShare} className="fa fa-2x fa-share-alt marginL5 align-middle action-item-label share-icon" />
            {renderShareBox()}
        </div>
    );
};

export default Share;